import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CtaLink } from "@/components/landing/CtaLink";
import { useAuth } from "@/lib/auth";
import sorvaineLogo from "@/assets/sorvaine-logo.png";
import sorvaineIcon from "@/assets/sorvaine-icon.png";
import sorvaineMobileIcon from "@/assets/sorvaine-mobile-icon.png.asset.json";



const navLinks = [
  { label: "Product", href: "#product" },
  { label: "Pricing", href: "#pricing" },
  { label: "Platform", href: "/platform", isRoute: true },
  { label: "Blog", href: "/blog", isRoute: true },
];

export function LandingNav() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();


  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href: string, isRoute?: boolean) => {
    setIsMobileMenuOpen(false);

    if (isRoute) {
      navigate(href);
      return;
    }

    if (window.location.pathname !== '/') {
      navigate('/' + href);
      return;
    }

    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      {/* Fixed Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-[height] duration-300 ${
          isScrolled ? "h-16" : "h-20"
        }`}
      >
        {/* Backdrop layer — soft gradient fade on mobile, solid blur on desktop */}
        <div
          aria-hidden
          className={`absolute inset-0 -z-10 transition-opacity duration-300 pointer-events-none ${
            isScrolled ? "opacity-100" : "opacity-0"
          }`}
        >
          {/* Mobile: soft top-down fade, no hard edge */}
          <div className="md:hidden absolute inset-0 bg-gradient-to-b from-black/85 via-black/55 to-transparent" />
          {/* Desktop: solid frosted bar */}
          <div className="hidden md:block absolute inset-0 bg-black/80 backdrop-blur-xl border-b border-white/[0.06]" />
        </div>
        <div className="h-full max-w-[1200px] mx-auto px-6 flex items-center justify-between relative">
          {/* Logo */}
          <Link to="/" className="flex items-center -ml-1">
            <img src={sorvaineMobileIcon.url} alt="Sorvaine" width={44} height={44} className="h-11 w-11 md:hidden" loading="eager" decoding="async" />
            <img src={sorvaineLogo} alt="Sorvaine logo" width={93} height={23} className="hidden md:block h-6 w-auto" loading="eager" decoding="async" />
          </Link>

          {/* Desktop Navigation - Centered (lg+) */}
          <div className="hidden lg:flex items-center gap-10 absolute left-1/2 -translate-x-1/2">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href, link.isRoute)}
                className="relative text-[15px] font-medium text-white/50 hover:text-white/90 transition-colors duration-300 inline-flex items-center gap-1.5"
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Right side - Desktop CTA (lg+) */}
          <div className="hidden lg:flex items-center gap-2">
            {user ? (
              <Link to="/dashboard">
                <Button className="rounded-md bg-primary hover:bg-primary/90 hover:-translate-y-[1px] px-6 h-9 !text-white font-semibold text-[15px] tracking-[0.01em] transition-all duration-200 shadow-[0_0_16px_hsl(var(--primary)/0.12)]">
                  Go to app
                  <ArrowRight className="ml-1.5 w-3.5 h-3.5" />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button
                    variant="ghost"
                    className="text-[15px] font-medium text-white/60 hover:text-white hover:bg-white/5 px-5"
                  >
                    Log in
                  </Button>
                </Link>
                <CtaLink to="/signup" cta="get_started" location="nav">
                  <Button className="rounded-md bg-primary hover:bg-primary/90 hover:-translate-y-[1px] px-6 h-9 !text-white font-semibold text-[15px] tracking-[0.01em] transition-all duration-200 shadow-[0_0_16px_hsl(var(--primary)/0.12)]">
                    Get started
                    <ArrowRight className="ml-1.5 w-3.5 h-3.5" />
                  </Button>
                </CtaLink>
              </>
            )}
          </div>


          {/* Tablet CTA (md to lg) - compact, links live in menu */}
          <div className="hidden md:flex lg:hidden items-center gap-2">
            {user ? (
              <Link to="/dashboard">
                <Button className="rounded-md bg-primary hover:bg-primary/90 px-5 h-9 !text-white font-semibold text-[15px] tracking-[0.01em] shadow-[0_0_16px_hsl(var(--primary)/0.12)]">
                  Go to app
                  <ArrowRight className="ml-1.5 w-3.5 h-3.5" />
                </Button>
              </Link>
            ) : (
              <CtaLink to="/signup" cta="get_started" location="nav_tablet">
                <Button className="rounded-md bg-primary hover:bg-primary/90 px-5 h-9 !text-white font-semibold text-[15px] tracking-[0.01em] shadow-[0_0_16px_hsl(var(--primary)/0.12)]">
                  Get started
                  <ArrowRight className="ml-1.5 w-3.5 h-3.5" />
                </Button>
              </CtaLink>
            )}

            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="text-white/60 hover:text-white transition-colors p-2"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="md:hidden text-white/60 hover:text-white transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed inset-0 z-50 bg-black/95 backdrop-blur-xl lg:hidden"
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="h-20 px-6 flex items-center justify-between border-b border-white/[0.06]">
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <img src={sorvaineIcon} alt="Sorvaine icon" className="h-8 w-8" loading="lazy" />
                </Link>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-white/60 hover:text-white transition-colors"
                  aria-label="Close menu"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Navigation Links */}
              <div className="flex-1 px-6 py-8">
                {navLinks.map((link) => (
                  <button
                    key={link.label}
                    onClick={() => handleNavClick(link.href, link.isRoute)}
                    className="flex w-full items-center gap-2 text-left text-base font-medium text-white/80 py-4 border-b border-white/[0.06] hover:text-white transition-colors"
                  >
                    {link.label}
                  </button>
                ))}
              </div>

              {/* Mobile CTA */}
              <div className="p-6 space-y-3 border-t border-white/[0.06]">
                {user ? (
                  <Link
                    to="/dashboard"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block"
                  >
                    <Button className="w-full h-11 rounded-md bg-primary hover:bg-primary/90 !text-white font-semibold text-[15px] tracking-[0.01em] inline-flex items-center justify-center">
                      Go to app
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                ) : (
                  <>
                    <CtaLink
                      to="/signup"
                      cta="get_started"
                      location="nav_mobile"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block"
                    >
                      <Button className="w-full h-11 rounded-md bg-primary hover:bg-primary/90 !text-white font-semibold text-[15px] tracking-[0.01em] inline-flex items-center justify-center">
                        Get started
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </CtaLink>
                    <Link
                      to="/login"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center justify-center text-sm text-white/60 hover:text-white/90 transition-colors h-11"
                    >
                      Log in
                    </Link>
                  </>
                )}
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
